import axios from 'axios';
import { Pnevmatika, PnevmatikaCreateInput, PnevmatikaFilters } from '../types/pnevmatika';

// Helper to decode JWT and check expiry
function isJwtExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true;
  }
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:3002` : 'http://localhost:3002');

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    if (isJwtExpired(token)) {
      localStorage.removeItem('auth_token');
      window.location.reload();
      return Promise.reject(new Error('Seja je potekla. Prijavite se znova.'));
    }
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const pnevmatikeApi = {
  getNextOznaka: async (): Promise<number> => {
    const res = await api.get('/pnevmatike/next-oznaka');
    return res.data.next;
  },
  // Get all pnevmatike with optional filters
  getAll: async (filters?: PnevmatikaFilters): Promise<Pnevmatika[]> => {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/pnevmatike?${params.toString()}`);
    return response.data;
  },

  // Get pnevmatika by ID
  getById: async (id: number): Promise<Pnevmatika> => {
    const response = await api.get(`/pnevmatike/${id}`);
    return response.data;
  },

  // Create new pnevmatika
  create: async (pnevmatika: PnevmatikaCreateInput): Promise<Pnevmatika> => {
    const response = await api.post('/pnevmatike', pnevmatika);
    return response.data;
  },

  // Update pnevmatika
  update: async (id: number, pnevmatika: Partial<PnevmatikaCreateInput>): Promise<Pnevmatika> => {
    const response = await api.put(`/pnevmatike/${id}`, pnevmatika);
    return response.data;
  },

  // Delete pnevmatika
  delete: async (id: number): Promise<void> => {
    await api.delete(`/pnevmatike/${id}`);
  },

  // Check if tire number exists
  checkTireNumber: async (oznaka: number, excludeId?: number): Promise<{ exists: boolean; pnevmatike: Pnevmatika[]; count: number }> => {
    const url = excludeId 
      ? `/pnevmatike/check/${oznaka}?excludeId=${excludeId}`
      : `/pnevmatike/check/${oznaka}`;
    
    const response = await api.get(url);
    return {
      exists: response.data.exists,
      pnevmatike: response.data.pnevmatike || [],
      count: response.data.count || 0
    };
  },
};

export const authApi = {
  login: async (username: string, password: string): Promise<{ token: string; user: { id: number; username: string } }> => {
    const res = await api.post('/auth/login', { username, password });
    const { token } = res.data;
    localStorage.setItem('auth_token', token);
    return res.data;
  },
  changePassword: async (currentPassword: string, newPassword: string, repeatNewPassword: string): Promise<{ success: boolean }> => {
    const res = await api.post('/auth/change-password', { currentPassword, newPassword, repeatNewPassword });
    return res.data;
  },
  logout: () => {
    localStorage.removeItem('auth_token');
  }
};
